import React, { useEffect, useState } from "react";
import { Stack, Button } from "react-bootstrap";
import Select from "react-select";
import { SegmentationManager } from "../segmentation";

interface SegmentationFiltersProps {
  segmentationManager: SegmentationManager | null;
  onEnableLabelmaps?: (progressCallback: (progress: number) => void) => void;
}

interface SelectOption {
  value: string;
  label: string;
}

const SegmentationFilters: React.FC<SegmentationFiltersProps> = ({
  segmentationManager,
  onEnableLabelmaps,
}) => {
  const [availableOwners, setAvailableOwners] = useState<SelectOption[]>([]);
  const [availableOrgans, setAvailableOrgans] = useState<SelectOption[]>([]);
  const [selectedOwners, setSelectedOwners] = useState<SelectOption[]>([]);
  const [selectedOrgans, setSelectedOrgans] = useState<SelectOption[]>([]);
  const [labelmapsEnabled, setLabelmapsEnabled] = useState<boolean>(false);
  const [labelmapProgress, setLabelmapProgress] = useState<number>(0);
  const [isEnabling, setIsEnabling] = useState<boolean>(false);

  // Initialize filter options when segmentationManager is available
  useEffect(() => {
    if (!segmentationManager) return;

    // Get available owners and organs from the segmentation manager
    const owners = [
      { value: "*", label: "All Students" },
      ...segmentationManager.getAvailableOwners().map((owner) => ({
        value: owner,
        label: owner,
      })),
    ];

    const organs = [
      { value: "*", label: "All Organs" },
      ...segmentationManager.getAvailableOrgans().map((organ) => ({
        value: organ,
        label: organ,
      })),
    ];

    setAvailableOwners(owners);
    setAvailableOrgans(organs);

    // Set default selections to "All" options only
    const defaultOwners = [{ value: "*", label: "All Students" }];
    const defaultOrgans = [{ value: "*", label: "All Organs" }];

    setSelectedOwners(defaultOwners);
    setSelectedOrgans(defaultOrgans);

    // Apply initial filters - show all students and all organs
    const ownerSet = new Set(segmentationManager.getAvailableOwners());
    const organSet = new Set(segmentationManager.getAvailableOrgans());

    segmentationManager.setFilters({
      selectedOwners: ownerSet,
      selectedOrgans: organSet,
    });
  }, [segmentationManager]);

  // Handle owner selection changes
  const handleOwnerChange = (selectedOptions: readonly SelectOption[]) => {
    if (!segmentationManager) return;

    const newSelection = selectedOptions || [];
    setSelectedOwners([...newSelection]);

    // Handle "All Students" selection
    let ownerSet: Set<string>;
    if (newSelection.some((option) => option.value === "*")) {
      // If "All Students" is selected, include all available owners
      ownerSet = new Set(segmentationManager.getAvailableOwners());
    } else {
      ownerSet = new Set(newSelection.map((option) => option.value));
    }

    const organSet = new Set(
      selectedOrgans
        .map((option) =>
          option.value === "*"
            ? segmentationManager.getAvailableOrgans()
            : [option.value],
        )
        .flat(),
    );

    segmentationManager.setFilters({
      selectedOwners: ownerSet,
      selectedOrgans: organSet,
    });
  };

  // Handle organ selection changes
  const handleOrganChange = (selectedOptions: readonly SelectOption[]) => {
    if (!segmentationManager) return;

    const newSelection = selectedOptions || [];
    setSelectedOrgans([...newSelection]);

    // Handle "All Organs" selection
    let organSet: Set<string>;
    if (newSelection.some((option) => option.value === "*")) {
      // If "All Organs" is selected, include all available organs
      organSet = new Set(segmentationManager.getAvailableOrgans());
    } else {
      organSet = new Set(newSelection.map((option) => option.value));
    }

    const ownerSet = new Set(
      selectedOwners
        .map((option) =>
          option.value === "*"
            ? segmentationManager.getAvailableOwners()
            : [option.value],
        )
        .flat(),
    );

    segmentationManager.setFilters({
      selectedOwners: ownerSet,
      selectedOrgans: organSet,
    });
  };

  // Handle enabling labelmaps for sagittal and coronal views
  const handleEnableLabelmaps = () => {
    if (!onEnableLabelmaps) return;

    setIsEnabling(true);
    setLabelmapProgress(0);

    const progressCallback = (progress: number) => {
      setLabelmapProgress(progress);
      if (progress >= 100) {
        setLabelmapsEnabled(true);
        setIsEnabling(false);
      }
    };

    onEnableLabelmaps(progressCallback);
  };

  return (
    <div
      style={{
        padding: "15px 20px",
        borderBottom: "1px solid var(--modal-header-border)",
        backgroundColor: "var(--sidebar-bg)",
        width: "100%",
      }}
    >
      <Stack gap={3} direction="horizontal" style={{ alignItems: "center" }}>
        <span
          style={{ minWidth: "80px", fontWeight: "bold", fontSize: "14px" }}
        >
          Students:
        </span>
        <Select
          placeholder={
            !segmentationManager ? "Loading..." : "Select students..."
          }
          value={selectedOwners}
          onChange={handleOwnerChange}
          options={availableOwners}
          isMulti
          isSearchable
          isClearable
          isDisabled={!segmentationManager}
          className="segmentation-filter-select"
          styles={{
            container: (provided) => ({
              ...provided,
              minWidth: "250px",
              flex: 1,
              maxWidth: "400px",
            }),
          }}
        />

        <span
          style={{ minWidth: "70px", fontWeight: "bold", fontSize: "14px" }}
        >
          Organs:
        </span>
        <Select
          placeholder={!segmentationManager ? "Loading..." : "Select organs..."}
          value={selectedOrgans}
          onChange={handleOrganChange}
          options={availableOrgans}
          isMulti
          isSearchable
          isClearable
          isDisabled={!segmentationManager}
          className="segmentation-filter-select"
          styles={{
            container: (provided) => ({
              ...provided,
              minWidth: "250px",
              flex: 1,
              maxWidth: "400px",
            }),
          }}
        />

        <Button
          variant={
            labelmapsEnabled
              ? "success"
              : isEnabling
                ? "warning"
                : "outline-primary"
          }
          size="sm"
          onClick={handleEnableLabelmaps}
          disabled={labelmapsEnabled || isEnabling || !segmentationManager}
          style={{
            minWidth: "180px",
            whiteSpace: "nowrap",
          }}
        >
          {labelmapsEnabled
            ? "✓ Interpolation Enabled"
            : isEnabling
              ? `Loading... ${labelmapProgress}%`
              : "Enable Interpolation"}
        </Button>
      </Stack>
    </div>
  );
};

export default SegmentationFilters;
